import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { 
  LayoutDashboard, 
  Users, 
  ArrowLeftRight, 
  Settings,
  RefreshCw,
  Shield,
  Scale,
  Calculator,
  LogOut,
  Eye,
  EyeOff,
  Check,
  X,
  AlertTriangle,
  FileText,
  Download,
  Search,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  Ban,
  Flag,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Filter,
  BarChart3,
  PieChart
} from "lucide-react";
import { 
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color scheme
const SBER = {
  green: '#21A038',
  greenDark: '#1A8F2D',
  greenLight: '#4DB45F',
  greenPale: '#E8F5E9',
  white: '#FFFFFF',
  bg: '#F5F5F5',
  dark: '#1A1A1A',
  gray: '#6B7280',
  grayLight: '#9CA3AF',
  grayPale: '#F3F4F6',
};

const roleConfig = {
  super_admin: { label: 'Суперадмин', icon: Shield, color: '#8B5CF6' },
  administrator: { label: 'Администратор', icon: Shield, color: '#8B5CF6' },
  admin: { label: 'Админ', icon: UserCheck, color: '#3B82F6' },
  kyc_manager: { label: 'KYC-менеджер', icon: UserCheck, color: '#10B981' },
  compliance: { label: 'Комплаенс', icon: UserCheck, color: '#10B981' },
  settlements: { label: 'Отдел расчётов', icon: DollarSign, color: '#F59E0B' },
  accounting: { label: 'Бухгалтерия', icon: Calculator, color: '#EC4899' },
  accountant: { label: 'Бухгалтер', icon: Calculator, color: '#EC4899' },
  manager: { label: 'Менеджер', icon: Users, color: '#6366F1' },
  lawyer: { label: 'Юрист', icon: Scale, color: '#EF4444' }
};

const tapAnimation = { scale: 0.97 };

// Card Component
const Card = ({ children, className = "", onClick, accent = false }) => (
  <motion.div
    whileTap={onClick ? tapAnimation : undefined}
    onClick={onClick}
    className={`rounded-2xl transition-all ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''} ${className}`}
    style={{ 
      background: accent ? `linear-gradient(135deg, ${SBER.green} 0%, ${SBER.greenDark} 100%)` : SBER.white,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    }}
  >
    {children}
  </motion.div>
);

// Login Screen
const LoginScreen = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        onLogin(res.data.token, res.data.role, res.data.username);
        toast.success('Успешный вход');
      } else {
        setError(res.data.message);
      }
    } catch (err) {
      setError('Ошибка подключения к серверу');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{ background: SBER.bg }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4" style={{ background: SBER.green }}>
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: SBER.dark }}>PolyTech Admin</h1>
          <p className="text-sm mt-1" style={{ color: SBER.gray }}>Панель управления</p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                Логин
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-green-500 outline-none"
                style={{ background: SBER.grayPale }}
                placeholder="Введите логин"
                data-testid="admin-login-username"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                Пароль
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border-0 focus:ring-2 focus:ring-green-500 outline-none pr-12"
                  style={{ background: SBER.grayPale }}
                  placeholder="Введите пароль"
                  data-testid="admin-login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? 
                    <EyeOff className="w-5 h-5" style={{ color: SBER.gray }} /> : 
                    <Eye className="w-5 h-5" style={{ color: SBER.gray }} />
                  }
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-xl text-sm text-red-700" style={{ background: '#FEE2E2' }}>
                {error}
              </div>
            )}

            <motion.button
              whileTap={tapAnimation}
              type="submit"
              disabled={loading || !username || !password}
              className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: SBER.green }}
              data-testid="admin-login-submit"
            >
              {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Войти'}
            </motion.button>
          </form>
        </Card>

        <p className="text-center mt-6 text-xs" style={{ color: SBER.grayLight }}>
          © 2024 PolyTech Wallet
        </p>
      </motion.div>
    </div>
  );
};

// Navigation for role
const RoleNavigation = ({ role, active, onNavigate }) => {
  const navItems = {
    super_admin: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'users', icon: Users, label: 'Пользователи' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'accountant', icon: Calculator, label: 'Финансы' },
      { id: 'reports', icon: BarChart3, label: 'Аналитика' },
      { id: 'settings', icon: Settings, label: 'Настройки' },
    ],
    administrator: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'users', icon: Users, label: 'Пользователи' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'accountant', icon: Calculator, label: 'Финансы' },
      { id: 'reports', icon: BarChart3, label: 'Аналитика' },
      { id: 'settings', icon: Settings, label: 'Настройки' },
    ],
    admin: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'users', icon: Users, label: 'Пользователи' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'reports', icon: BarChart3, label: 'Аналитика' },
      { id: 'settings', icon: Settings, label: 'Настройки' },
    ],
    kyc_manager: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'aml', icon: AlertTriangle, label: 'AML' },
      { id: 'users', icon: Users, label: 'Просмотр' },
    ],
    compliance: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'aml', icon: AlertTriangle, label: 'AML' },
    ],
    settlements: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'accountant', icon: Calculator, label: 'Финансы' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'settings', icon: Settings, label: 'Настройки' },
    ],
    accounting: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'accountant', icon: Calculator, label: 'Финансы' },
      { id: 'reports', icon: FileText, label: 'Отчёты' },
    ],
    accountant: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'accountant', icon: Calculator, label: 'Финансы' },
      { id: 'reports', icon: FileText, label: 'Отчёты' },
    ],
    manager: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'users', icon: Users, label: 'Просмотр' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'settings', icon: Settings, label: 'Настройки' },
    ],
    lawyer: [
      { id: 'dashboard', icon: LayoutDashboard, label: 'Главная' },
      { id: 'compliance', icon: UserCheck, label: 'KYC' },
      { id: 'lawyer', icon: Scale, label: 'Блокировки' },
      { id: 'complaints', icon: FileText, label: 'Жалобы' },
    ]
  };

  const items = navItems[role] || navItems.administrator;
  // Show all items for better UX, they'll wrap or scroll if needed
  const visibleItems = items;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 px-2 py-2 flex justify-around items-center"
      style={{ 
        background: SBER.white, 
        borderTop: `1px solid ${SBER.grayPale}`,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
      }}
    >
      {visibleItems.map((item) => (
        <motion.button
          key={item.id}
          whileTap={tapAnimation}
          onClick={() => onNavigate(item.id)}
          className="flex flex-col items-center py-1 px-2"
        >
          <item.icon 
            className="w-5 h-5 mb-1" 
            style={{ color: active === item.id ? SBER.green : SBER.grayLight }}
            strokeWidth={active === item.id ? 2.5 : 2}
          />
          <span 
            className="text-[9px] font-medium"
            style={{ color: active === item.id ? SBER.green : SBER.grayLight }}
          >
            {item.label}
          </span>
        </motion.button>
      ))}
    </div>
  );
};

// KYC Detail Modal Component with Comments and Document Requests
const KYCDetailModal = ({ user, token, onClose, onApprove, onReject, onUpdate }) => {
  const docs = user?.kyc_documents || {};
  const [activeTab, setActiveTab] = useState('info'); // info, comment, request
  const [comment, setComment] = useState('');
  const [selectedDocField, setSelectedDocField] = useState('');
  const [requestDocs, setRequestDocs] = useState([]);
  const [requestMessage, setRequestMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  
  // Document options for request
  const docOptions = [
    'Паспорт директора (все страницы)',
    'Устав компании',
    'Выписка из ЕГРЮЛ (актуальная)',
    'Свидетельство ИНН',
    'Свидетельство ОГРН',
    'Приказ о назначении директора',
    'Доверенность (если применимо)',
    'Договор аренды офиса',
    'Справка из банка',
    'Другой документ'
  ];

  // Fetch existing comments
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await axios.get(`${API}/admin/compliance/kyc/${user.telegram_id}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setComments(res.data.comments || []);
      } catch (err) {}
    };
    if (user?.telegram_id && token) fetchComments();
  }, [user, token]);
  
  const InfoRow = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-gray-100">
      <span className="text-sm" style={{ color: SBER.gray }}>{label}</span>
      <span className="text-sm font-medium text-right" style={{ color: SBER.dark }}>{value || '—'}</span>
    </div>
  );

  const DocumentButton = ({ name, filename, fieldName }) => (
    <div className="flex gap-2">
      <motion.button
        whileTap={tapAnimation}
        onClick={() => window.open(`${BACKEND_URL}/api/documents/${filename}`, '_blank')}
        className="flex-1 p-3 rounded-xl flex items-center justify-between"
        style={{ background: SBER.grayPale }}
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5" style={{ color: SBER.green }} />
          <span className="text-sm font-medium" style={{ color: SBER.dark }}>{name}</span>
        </div>
        <Eye className="w-5 h-5" style={{ color: SBER.gray }} />
      </motion.button>
      <motion.button
        whileTap={tapAnimation}
        onClick={() => {
          setSelectedDocField(fieldName || name);
          setActiveTab('comment');
        }}
        className="w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ background: '#FEF3C7' }}
        title="Добавить замечание"
      >
        <AlertTriangle className="w-4 h-4 text-amber-600" />
      </motion.button>
    </div>
  );

  const handleAddComment = async () => {
    if (!comment.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/compliance/kyc/${user.telegram_id}/comment`, {
        comment: comment,
        document_field: selectedDocField || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Замечание отправлено клиенту');
      setComment('');
      setSelectedDocField('');
      setActiveTab('info');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Ошибка отправки');
    }
    setLoading(false);
  };

  const handleRequestDocuments = async () => {
    if (requestDocs.length === 0 || !requestMessage.trim()) {
      toast.error('Выберите документы и укажите причину');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/compliance/kyc/${user.telegram_id}/request-documents`, {
        documents: requestDocs,
        message: requestMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Запрос отправлен клиенту');
      setRequestDocs([]);
      setRequestMessage('');
      setActiveTab('info');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Ошибка отправки');
    }
    setLoading(false);
  };

  const toggleDocRequest = (doc) => {
    setRequestDocs(prev => 
      prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl"
        style={{ background: SBER.white }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 p-4 border-b z-10" style={{ background: SBER.white }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>Заявка на верификацию</h2>
            <motion.button whileTap={tapAnimation} onClick={onClose}>
              <X className="w-6 h-6" style={{ color: SBER.gray }} />
            </motion.button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'info' ? 'text-white' : ''}`}
              style={{ background: activeTab === 'info' ? SBER.green : SBER.grayPale, color: activeTab === 'info' ? 'white' : SBER.dark }}
            >
              Данные
            </button>
            <button
              onClick={() => setActiveTab('comment')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'comment' ? 'text-white' : ''}`}
              style={{ background: activeTab === 'comment' ? '#F59E0B' : SBER.grayPale, color: activeTab === 'comment' ? 'white' : SBER.dark }}
            >
              Замечание
            </button>
            <button
              onClick={() => setActiveTab('request')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'request' ? 'text-white' : ''}`}
              style={{ background: activeTab === 'request' ? '#3B82F6' : SBER.grayPale, color: activeTab === 'request' ? 'white' : SBER.dark }}
            >
              Запросить
            </button>
          </div>
        </div>

        <div className="p-4">
          {/* INFO TAB */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Previous Comments */}
              {comments.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    Отправленные замечания ({comments.length})
                  </h3>
                  <div className="space-y-2">
                    {comments.map((c, idx) => (
                      <div key={idx} className="p-3 rounded-xl text-sm" style={{ background: '#FEF3C7' }}>
                        <p className="text-amber-800">{c.comment}</p>
                        {c.document_field && (
                          <p className="text-xs text-amber-600 mt-1">К документу: {c.document_field}</p>
                        )}
                        <p className="text-xs text-amber-500 mt-1">
                          {new Date(c.created_at).toLocaleString('ru-RU')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Company Info */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: SBER.dark }}>
                  <Shield className="w-5 h-5" style={{ color: SBER.green }} />
                  Данные компании
                </h3>
                <Card className="p-3">
                  <InfoRow label="Название" value={docs.company_name} />
                  <InfoRow label="ИНН" value={docs.inn} />
                  <InfoRow label="ОГРН" value={docs.ogrn} />
                  <InfoRow label="Юр. адрес" value={docs.legal_address} />
                </Card>
              </div>

              {/* Director Info */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: SBER.dark }}>
                  <UserCheck className="w-5 h-5" style={{ color: SBER.green }} />
                  Данные руководителя
                </h3>
                <Card className="p-3">
                  <InfoRow label="ФИО директора" value={docs.director_name} />
                  <InfoRow label="Паспорт" value={docs.director_passport} />
                  <InfoRow label="Телефон" value={docs.phone} />
                  <InfoRow label="Email" value={docs.email} />
                </Card>
              </div>

              {/* Bank Info */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: SBER.dark }}>
                  <DollarSign className="w-5 h-5" style={{ color: SBER.green }} />
                  Банковские реквизиты
                </h3>
                <Card className="p-3">
                  <InfoRow label="Банк" value={docs.bank_name} />
                  <InfoRow label="Р/с" value={docs.bank_account} />
                  <InfoRow label="БИК" value={docs.bank_bik} />
                </Card>
              </div>

              {/* Documents */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: SBER.dark }}>
                  <FileText className="w-5 h-5" style={{ color: SBER.green }} />
                  Приложенные документы
                </h3>
                <div className="space-y-2">
                  {docs.passport_scan && (
                    <DocumentButton name="Скан паспорта директора" filename={docs.passport_scan} fieldName="passport_scan" />
                  )}
                  {docs.company_charter && (
                    <DocumentButton name="Устав компании" filename={docs.company_charter} fieldName="company_charter" />
                  )}
                  {docs.egrul_extract && (
                    <DocumentButton name="Выписка из ЕГРЮЛ" filename={docs.egrul_extract} fieldName="egrul_extract" />
                  )}
                </div>
              </div>

              {/* Submission Info */}
              <div className="text-center text-xs" style={{ color: SBER.gray }}>
                Заявка подана: {docs.documents_submitted_at ? new Date(docs.documents_submitted_at).toLocaleString('ru-RU') : '—'}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => onReject(user.telegram_id)}
                  className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                  style={{ background: '#FEE2E2', color: '#DC2626' }}
                >
                  <X className="w-5 h-5" />
                  Отклонить
                </motion.button>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => onApprove(user.telegram_id)}
                  className="flex-1 py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2"
                  style={{ background: SBER.green }}
                >
                  <Check className="w-5 h-5" />
                  Одобрить
                </motion.button>
              </div>
            </div>
          )}

          {/* COMMENT TAB */}
          {activeTab === 'comment' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl" style={{ background: '#FEF3C7' }}>
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Замечание будет отправлено клиенту без указания автора
                </p>
              </div>

              {selectedDocField && (
                <div className="p-3 rounded-xl" style={{ background: SBER.grayPale }}>
                  <p className="text-xs" style={{ color: SBER.gray }}>Замечание к документу:</p>
                  <p className="font-medium" style={{ color: SBER.dark }}>{selectedDocField}</p>
                  <button 
                    onClick={() => setSelectedDocField('')}
                    className="text-xs text-red-600 mt-1"
                  >
                    Убрать привязку
                  </button>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Текст замечания
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Опишите, что нужно исправить..."
                  className="w-full px-4 py-3 rounded-xl border-0 resize-none h-32"
                  style={{ background: SBER.grayPale }}
                />
              </div>

              <motion.button
                whileTap={tapAnimation}
                onClick={handleAddComment}
                disabled={loading || !comment.trim()}
                className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#F59E0B' }}
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <AlertTriangle className="w-5 h-5" />}
                Отправить замечание
              </motion.button>
            </div>
          )}

          {/* REQUEST TAB */}
          {activeTab === 'request' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl" style={{ background: '#DBEAFE' }}>
                <p className="text-sm text-blue-800">
                  <FileText className="w-4 h-4 inline mr-2" />
                  Запрос будет отправлен клиенту без указания автора
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Выберите документы для запроса
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {docOptions.map((doc, idx) => (
                    <label 
                      key={idx}
                      className="flex items-center gap-3 p-3 rounded-xl cursor-pointer"
                      style={{ background: requestDocs.includes(doc) ? SBER.greenPale : SBER.grayPale }}
                    >
                      <input
                        type="checkbox"
                        checked={requestDocs.includes(doc)}
                        onChange={() => toggleDocRequest(doc)}
                        className="w-5 h-5 rounded"
                      />
                      <span className="text-sm" style={{ color: SBER.dark }}>{doc}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: SBER.dark }}>
                  Причина запроса / комментарий
                </label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Укажите, почему требуются эти документы..."
                  className="w-full px-4 py-3 rounded-xl border-0 resize-none h-24"
                  style={{ background: SBER.grayPale }}
                />
              </div>

              <motion.button
                whileTap={tapAnimation}
                onClick={handleRequestDocuments}
                disabled={loading || requestDocs.length === 0 || !requestMessage.trim()}
                className="w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#3B82F6' }}
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                Запросить документы ({requestDocs.length})
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Compliance Module - KYC Queue
const ComplianceModule = ({ token }) => {
  const [queue, setQueue] = useState({ pending: [], rejected: [] });
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/compliance/kyc-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueue(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки очереди KYC');
    }
    setLoading(false);
  };

  useEffect(() => { fetchQueue(); }, []);

  const handleApprove = async (telegramId) => {
    try {
      await axios.post(`${API}/admin/compliance/kyc/${telegramId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('KYC одобрен');
      setSelectedUser(null);
      fetchQueue();
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  const handleReject = async (telegramId) => {
    const reason = window.prompt('Причина отклонения:');
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/admin/compliance/kyc/${telegramId}/reject?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('KYC отклонён');
      setSelectedUser(null);
      fetchQueue();
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      {/* KYC Detail Modal */}
      <AnimatePresence>
        {selectedUser && (
          <KYCDetailModal
            user={selectedUser}
            token={token}
            onClose={() => setSelectedUser(null)}
            onApprove={handleApprove}
            onReject={handleReject}
            onUpdate={fetchQueue}
          />
        )}
      </AnimatePresence>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>Очередь KYC</h2>
          <motion.button whileTap={tapAnimation} onClick={fetchQueue}>
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>

        {queue.pending.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: SBER.green }} />
            <p style={{ color: SBER.gray }}>Нет заявок на проверку</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queue.pending.map((user, idx) => (
              <motion.div 
                key={idx} 
                className="p-3 rounded-xl cursor-pointer"
                style={{ background: SBER.grayPale }}
                whileTap={tapAnimation}
                onClick={() => setSelectedUser(user)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: SBER.dark }}>
                      {user.kyc_documents?.company_name || user.first_name || 'Пользователь'}
                    </p>
                    <p className="text-xs" style={{ color: SBER.gray }}>
                      ИНН: {user.kyc_documents?.inn || '—'} • @{user.username || user.telegram_id}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: SBER.gray }} />
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </Card>

      {/* Revision Needed Section */}
      {queue.revision_needed && queue.revision_needed.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#F59E0B' }}>
            <Clock className="w-5 h-5" />
            На доработке ({queue.revision_needed.length})
          </h3>
          <div className="space-y-2">
            {queue.revision_needed.map((user, idx) => (
              <motion.div 
                key={idx} 
                className="p-3 rounded-xl cursor-pointer"
                style={{ background: '#FEF3C7' }}
                whileTap={tapAnimation}
                onClick={() => setSelectedUser(user)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800">
                      {user.kyc_documents?.company_name || user.first_name || 'Пользователь'}
                    </p>
                    <p className="text-xs text-amber-600">
                      {user.kyc_status === 'DOCUMENTS_REQUESTED' ? 'Запрошены документы' : 'Отправлены замечания'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-amber-500" />
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      )}

      {queue.rejected && queue.rejected.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Отклонённые ({queue.rejected.length})</h3>
          <div className="space-y-2">
            {queue.rejected.map((user, idx) => (
              <div key={idx} className="p-2 rounded-lg flex items-center justify-between" style={{ background: '#FEE2E2' }}>
                <span className="text-sm text-red-800">{user.first_name || user.telegram_id}</span>
                <span className="text-xs text-red-600">{user.kyc_reject_reason || 'Без причины'}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// Users Management Module
const UsersModule = ({ token, role }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '15',
        search,
        kyc_status: kycFilter
      });
      const res = await axios.get(`${API}/admin/users/full-list?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users || []);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Ошибка загрузки');
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, [page, search, kycFilter]);

  const kycStatusMap = {
    NOT_STARTED: { label: "Не начата", bg: SBER.grayPale, color: SBER.gray },
    PENDING: { label: "На проверке", bg: "#FEF3C7", color: "#F59E0B" },
    APPROVED: { label: "Пройдена", bg: SBER.greenPale, color: SBER.green },
    REJECTED: { label: "Отклонена", bg: "#FEE2E2", color: "#EF4444" },
    DOCUMENTS_REQUESTED: { label: "Запрошены документы", bg: "#DBEAFE", color: "#3B82F6" }
  };

  const handleUpdateKyc = async (userId, status, reason = '') => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/update-kyc?status=${status}&reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('KYC статус обновлён');
      fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleBlock = async (userId) => {
    const reason = window.prompt('Причина блокировки:');
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/block?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь заблокирован');
      fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь разблокирован');
      fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <Search className="w-5 h-5" style={{ color: SBER.grayLight }} />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Поиск по имени или ID"
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: SBER.dark }}
          />
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setShowFilters(!showFilters)}
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: showFilters ? SBER.green : SBER.grayPale }}
          >
            <Filter className="w-4 h-4" style={{ color: showFilters ? 'white' : SBER.gray }} />
          </motion.button>
        </div>
      </Card>

      {/* Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <Card className="p-4">
              <label className="text-xs font-medium mb-1 block" style={{ color: SBER.gray }}>KYC статус</label>
              <select
                value={kycFilter}
                onChange={(e) => { setKycFilter(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: SBER.grayPale }}
              >
                <option value="">Все</option>
                <option value="NOT_STARTED">Не начата</option>
                <option value="PENDING">На проверке</option>
                <option value="APPROVED">Пройдена</option>
                <option value="REJECTED">Отклонена</option>
              </select>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Users List */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin" style={{ color: SBER.green }} />
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((user) => {
            const kyc = kycStatusMap[user.kyc_status] || kycStatusMap.NOT_STARTED;
            const userId = user.telegram_id || user.user_id || user.id;
            return (
              <Card 
                key={userId} 
                className="p-4" 
                onClick={() => setSelectedUser(user)}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ background: user.is_blocked ? '#EF4444' : SBER.green }}
                  >
                    {(user.first_name?.[0] || user.username?.[0] || 'U').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate" style={{ color: SBER.dark }}>
                      {user.first_name || user.username || 'Пользователь'}
                      {user.is_blocked && <span className="text-red-500 text-xs ml-1">ЗАБЛОК.</span>}
                    </p>
                    <p className="text-xs" style={{ color: SBER.grayLight }}>
                      ID: {userId}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">{(user.balance_usdt || 0).toFixed(2)} USDT</p>
                    <span 
                      className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{ background: kyc.bg, color: kyc.color }}
                    >
                      {kyc.label}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color: SBER.grayLight }} />
                </div>
              </Card>
            );
          })}
          
          {users.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" style={{ color: SBER.grayLight }} />
              <p style={{ color: SBER.grayLight }}>Пользователи не найдены</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 rounded-lg disabled:opacity-50"
            style={{ background: SBER.grayPale }}
          >
            ←
          </motion.button>
          <span className="px-4 py-2 text-sm" style={{ color: SBER.dark }}>
            {page} / {totalPages}
          </span>
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 rounded-lg disabled:opacity-50"
            style={{ background: SBER.grayPale }}
          >
            →
          </motion.button>
        </div>
      )}

      {/* User Detail Modal - Enhanced */}
      <AnimatePresence>
        {selectedUser && (
          <UserDetailModal 
            user={selectedUser}
            token={token}
            kycStatusMap={kycStatusMap}
            onClose={() => setSelectedUser(null)}
            onUpdateKyc={handleUpdateKyc}
            onBlock={handleBlock}
            onUnblock={handleUnblock}
            onRefresh={fetchUsers}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Enhanced User Detail Modal Component
const UserDetailModal = ({ user, token, kycStatusMap, onClose, onUpdateKyc, onBlock, onUnblock, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [fullProfile, setFullProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFullProfile = async () => {
      try {
        const userId = user.telegram_id || user.user_id || user.id;
        const res = await axios.get(`${API}/admin/users/${userId}/full-profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFullProfile(res.data);
      } catch (err) {
        console.error('Error fetching full profile:', err);
      }
      setLoading(false);
    };
    fetchFullProfile();
  }, [user, token]);

  const userData = fullProfile?.user || user;
  const transactions = fullProfile?.transactions || [];
  const stats = fullProfile?.stats || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = '₽') => {
    return `${(amount || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2 })} ${currency}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl bg-white"
        onClick={e => e.stopPropagation()}
        data-testid="user-detail-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 border-b" style={{ background: SBER.white }}>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>Профиль пользователя</h2>
            <motion.button whileTap={tapAnimation} onClick={onClose}>
              <X className="w-6 h-6" style={{ color: SBER.gray }} />
            </motion.button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('info')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all`}
              style={{ background: activeTab === 'info' ? SBER.green : SBER.grayPale, color: activeTab === 'info' ? 'white' : SBER.dark }}
              data-testid="tab-info"
            >
              Информация
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all`}
              style={{ background: activeTab === 'transactions' ? SBER.green : SBER.grayPale, color: activeTab === 'transactions' ? 'white' : SBER.dark }}
              data-testid="tab-transactions"
            >
              Транзакции
            </button>
            <button
              onClick={() => setActiveTab('actions')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all`}
              style={{ background: activeTab === 'actions' ? SBER.green : SBER.grayPale, color: activeTab === 'actions' ? 'white' : SBER.dark }}
              data-testid="tab-actions"
            >
              Действия
            </button>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin" style={{ color: SBER.green }} />
            </div>
          ) : (
            <>
              {/* INFO TAB */}
              {activeTab === 'info' && (
                <div className="space-y-4">
                  {/* User Header */}
                  <div className="flex items-center gap-4 p-4 rounded-xl" style={{ background: SBER.grayPale }}>
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                      style={{ background: userData.is_blocked ? '#EF4444' : SBER.green }}
                    >
                      {(userData.first_name?.[0] || userData.username?.[0] || 'U').toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-lg" style={{ color: SBER.dark }}>
                        {userData.first_name} {userData.last_name || ''}
                      </p>
                      <p className="text-sm" style={{ color: SBER.gray }}>
                        @{userData.username || 'Без username'}
                      </p>
                      {userData.is_blocked && (
                        <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 mt-1 inline-block">
                          ЗАБЛОКИРОВАН
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Basic Info */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Основная информация</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm" style={{ color: SBER.gray }}>Telegram ID</span>
                        <span className="text-sm font-medium" style={{ color: SBER.dark }}>{userData.telegram_id || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm" style={{ color: SBER.gray }}>User ID</span>
                        <span className="text-sm font-medium" style={{ color: SBER.dark }}>{userData.user_id || userData.id || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm" style={{ color: SBER.gray }}>Телефон</span>
                        <span className="text-sm font-medium" style={{ color: SBER.dark }}>{userData.phone || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm" style={{ color: SBER.gray }}>Email</span>
                        <span className="text-sm font-medium" style={{ color: SBER.dark }}>{userData.email || '—'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm" style={{ color: SBER.gray }}>Регистрация</span>
                        <span className="text-sm font-medium" style={{ color: SBER.dark }}>{formatDate(userData.created_at)}</span>
                      </div>
                    </div>
                  </Card>

                  {/* Balance */}
                  <Card className="p-4" accent>
                    <h3 className="font-semibold mb-3 text-white">Баланс</h3>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-white">{(userData.balance_usdt || 0).toFixed(4)} USDT</p>
                      {userData.balance_reserved > 0 && (
                        <p className="text-sm text-white/70 mt-1">Зарезервировано: {userData.balance_reserved.toFixed(4)} USDT</p>
                      )}
                    </div>
                  </Card>

                  {/* KYC Status */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>KYC Статус</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm" style={{ color: SBER.gray }}>Текущий статус</span>
                        <span 
                          className="text-xs px-3 py-1 rounded-full font-medium"
                          style={{ 
                            background: kycStatusMap[userData.kyc_status]?.bg || SBER.grayPale,
                            color: kycStatusMap[userData.kyc_status]?.color || SBER.gray
                          }}
                        >
                          {kycStatusMap[userData.kyc_status]?.label || userData.kyc_status || 'Не начата'}
                        </span>
                      </div>
                      {userData.kyc_reject_reason && (
                        <div className="p-2 rounded-lg text-sm" style={{ background: '#FEE2E2', color: '#DC2626' }}>
                          Причина отказа: {userData.kyc_reject_reason}
                        </div>
                      )}
                      {userData.kyc_updated_at && (
                        <p className="text-xs" style={{ color: SBER.grayLight }}>
                          Обновлено: {formatDate(userData.kyc_updated_at)} ({userData.kyc_updated_by || 'система'})
                        </p>
                      )}
                    </div>
                  </Card>

                  {/* Stats */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Статистика</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 rounded-lg" style={{ background: SBER.greenPale }}>
                        <p className="text-lg font-bold" style={{ color: SBER.green }}>{formatCurrency(stats.total_topup)}</p>
                        <p className="text-xs" style={{ color: SBER.gray }}>Всего пополнено</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: '#FEF3C7' }}>
                        <p className="text-lg font-bold text-amber-700">{formatCurrency(stats.total_withdraw)}</p>
                        <p className="text-xs text-amber-600">Всего выведено</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: SBER.grayPale }}>
                        <p className="text-lg font-bold" style={{ color: SBER.dark }}>{stats.transaction_count || 0}</p>
                        <p className="text-xs" style={{ color: SBER.gray }}>Транзакций</p>
                      </div>
                      <div className="text-center p-3 rounded-lg" style={{ background: stats.net_flow >= 0 ? SBER.greenPale : '#FEE2E2' }}>
                        <p className="text-lg font-bold" style={{ color: stats.net_flow >= 0 ? SBER.green : '#DC2626' }}>
                          {stats.net_flow >= 0 ? '+' : ''}{formatCurrency(stats.net_flow)}
                        </p>
                        <p className="text-xs" style={{ color: SBER.gray }}>Нетто</p>
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* TRANSACTIONS TAB */}
              {activeTab === 'transactions' && (
                <div className="space-y-3">
                  {transactions.length === 0 ? (
                    <div className="text-center py-8">
                      <ArrowLeftRight className="w-12 h-12 mx-auto mb-2 opacity-50" style={{ color: SBER.grayLight }} />
                      <p style={{ color: SBER.grayLight }}>Нет транзакций</p>
                    </div>
                  ) : (
                    transactions.map((tx, idx) => (
                      <div 
                        key={idx} 
                        className="p-3 rounded-xl"
                        style={{ background: SBER.grayPale }}
                        data-testid={`transaction-${idx}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {tx.type?.includes('TOPUP') ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-amber-500" />
                            )}
                            <span className="text-sm font-medium" style={{ color: SBER.dark }}>
                              {tx.type || 'Транзакция'}
                            </span>
                          </div>
                          <span 
                            className={`text-xs px-2 py-0.5 rounded ${
                              tx.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                              tx.status === 'FAILED' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {tx.status || 'PENDING'}
                          </span>
                        </div>
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-sm" style={{ color: SBER.gray }}>
                              {tx.amount_rub ? `${tx.amount_rub.toLocaleString()} ₽` : ''}
                              {tx.amount_usdt ? ` • ${tx.amount_usdt.toFixed(4)} USDT` : ''}
                            </p>
                            <p className="text-xs" style={{ color: SBER.grayLight }}>
                              {formatDate(tx.created_at)}
                            </p>
                          </div>
                          {tx.fee_rub > 0 && (
                            <p className="text-xs" style={{ color: SBER.gray }}>
                              Комиссия: {tx.fee_rub.toFixed(2)} ₽
                            </p>
                          )}
                        </div>
                        {tx.id && (
                          <p className="text-xs mt-1 truncate" style={{ color: SBER.grayLight }}>
                            ID: {tx.id}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* ACTIONS TAB */}
              {activeTab === 'actions' && (
                <div className="space-y-4">
                  {/* KYC Status Change */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Изменить KYC статус</h3>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(kycStatusMap).map(([status, info]) => (
                        <motion.button
                          key={status}
                          whileTap={tapAnimation}
                          onClick={() => {
                            const reason = status === 'REJECTED' ? window.prompt('Причина отклонения:') : '';
                            if (status === 'REJECTED' && !reason) return;
                            onUpdateKyc(userData.telegram_id || userData.user_id, status, reason);
                          }}
                          className="px-3 py-2 rounded-lg text-xs font-medium"
                          style={{ 
                            background: userData.kyc_status === status ? info.color : info.bg,
                            color: userData.kyc_status === status ? 'white' : info.color
                          }}
                          data-testid={`kyc-btn-${status}`}
                        >
                          {info.label}
                        </motion.button>
                      ))}
                    </div>
                  </Card>

                  {/* Block/Unblock */}
                  <Card className="p-4">
                    <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Блокировка</h3>
                    {userData.is_blocked ? (
                      <motion.button
                        whileTap={tapAnimation}
                        onClick={() => onUnblock(userData.telegram_id || userData.user_id)}
                        className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                        style={{ background: SBER.greenPale, color: SBER.green }}
                        data-testid="unblock-user-btn"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Разблокировать пользователя
                      </motion.button>
                    ) : (
                      <motion.button
                        whileTap={tapAnimation}
                        onClick={() => onBlock(userData.telegram_id || userData.user_id)}
                        className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                        style={{ background: '#FEE2E2', color: '#DC2626' }}
                        data-testid="block-user-btn"
                      >
                        <Ban className="w-5 h-5" />
                        Заблокировать пользователя
                      </motion.button>
                    )}
                    {userData.block_reason && (
                      <p className="text-xs mt-2 text-red-600">
                        Причина: {userData.block_reason}
                      </p>
                    )}
                  </Card>

                  {/* AML Flag */}
                  {fullProfile?.aml_flags?.length > 0 && (
                    <Card className="p-4">
                      <h3 className="font-semibold mb-3 text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        AML Флаги ({fullProfile.aml_flags.length})
                      </h3>
                      <div className="space-y-2">
                        {fullProfile.aml_flags.map((flag, idx) => (
                          <div key={idx} className="p-2 rounded-lg text-sm" style={{ background: '#FEF3C7' }}>
                            <p className="text-amber-800">{flag.reason || flag.flag_type}</p>
                            <p className="text-xs text-amber-600">{formatDate(flag.created_at || flag.flagged_at)}</p>
                          </div>
                        ))}
                      </div>
                    </Card>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// AML Module
const AMLModule = ({ token }) => {
  const [data, setData] = useState({ suspicious_transactions: [], frequent_users: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/compliance/aml-flags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      toast.error('Ошибка загрузки');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleFlag = async (telegramId) => {
    const reason = window.prompt('Причина флага:');
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/admin/compliance/flag-user/${telegramId}?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь отмечен');
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>AML Мониторинг</h2>
          <motion.button whileTap={tapAnimation} onClick={fetchData}>
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-xl text-center" style={{ background: '#FEF3C7' }}>
            <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-amber-600" />
            <p className="text-xl font-bold text-amber-800">{data.suspicious_transactions?.length || 0}</p>
            <p className="text-xs text-amber-700">Подозрительных</p>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ background: '#DBEAFE' }}>
            <Users className="w-6 h-6 mx-auto mb-1 text-blue-600" />
            <p className="text-xl font-bold text-blue-800">{data.frequent_users?.length || 0}</p>
            <p className="text-xs text-blue-700">Частая активность</p>
          </div>
        </div>
      </Card>

      {data.suspicious_transactions?.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Крупные транзакции</h3>
          <div className="space-y-2">
            {data.suspicious_transactions.slice(0, 10).map((tx, idx) => (
              <div key={idx} className="p-3 rounded-lg flex items-center justify-between" style={{ background: '#FEF3C7' }}>
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {tx.amount_rub?.toLocaleString()} ₽
                  </p>
                  <p className="text-xs text-amber-600">{tx.flag_reason}</p>
                </div>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => handleFlag(tx.telegram_id)}
                  className="px-3 py-1 rounded-lg text-xs font-medium"
                  style={{ background: '#FEE2E2', color: '#DC2626' }}
                >
                  <Flag className="w-4 h-4" />
                </motion.button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

// Lawyer Module - Blocks
const LawyerModule = ({ token }) => {
  const [blocked, setBlocked] = useState([]);
  const [searchId, setSearchId] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchBlocked = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/lawyer/blocked-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocked(res.data.blocked_users);
    } catch (err) {
      toast.error('Ошибка загрузки');
    }
    setLoading(false);
  };

  useEffect(() => { fetchBlocked(); }, []);

  const handleBlock = async () => {
    if (!searchId) return;
    const reason = window.prompt('Причина блокировки:');
    if (reason === null) return;
    
    try {
      await axios.post(`${API}/admin/lawyer/block-user/${searchId}?reason=${encodeURIComponent(reason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь заблокирован');
      setSearchId('');
      fetchBlocked();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleUnblock = async (telegramId) => {
    try {
      await axios.post(`${API}/admin/lawyer/unblock-user/${telegramId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь разблокирован');
      fetchBlocked();
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: SBER.dark }}>Блокировка пользователей</h2>
        
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={searchId}
            onChange={(e) => setSearchId(e.target.value)}
            placeholder="Telegram ID"
            className="flex-1 px-4 py-2 rounded-xl border-0 outline-none"
            style={{ background: SBER.grayPale }}
            data-testid="block-user-input"
          />
          <motion.button
            whileTap={tapAnimation}
            onClick={handleBlock}
            className="px-4 py-2 rounded-xl font-medium text-white"
            style={{ background: '#DC2626' }}
            data-testid="block-user-btn"
          >
            <Ban className="w-5 h-5" />
          </motion.button>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ color: SBER.dark }}>
            Заблокированные ({blocked.length})
          </h3>
          <motion.button whileTap={tapAnimation} onClick={fetchBlocked}>
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>

        {blocked.length === 0 ? (
          <div className="text-center py-6">
            <CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: SBER.green }} />
            <p className="text-sm" style={{ color: SBER.gray }}>Нет заблокированных</p>
          </div>
        ) : (
          <div className="space-y-2">
            {blocked.map((user, idx) => (
              <div key={idx} className="p-3 rounded-xl flex items-center justify-between" style={{ background: '#FEE2E2' }}>
                <div>
                  <p className="font-medium text-red-800">{user.username || user.telegram_id}</p>
                  <p className="text-xs text-red-600">{user.block_reason || 'Без причины'}</p>
                </div>
                <motion.button
                  whileTap={tapAnimation}
                  onClick={() => handleUnblock(user.telegram_id)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ background: SBER.greenPale, color: SBER.green }}
                  data-testid={`unblock-${user.telegram_id}`}
                >
                  Разблокировать
                </motion.button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// Complaints Module
const ComplaintsModule = ({ token }) => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/lawyer/complaints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComplaints(res.data.complaints);
    } catch (err) {
      toast.error('Ошибка загрузки');
    }
    setLoading(false);
  };

  useEffect(() => { fetchComplaints(); }, []);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>Жалобы пользователей</h2>
          <motion.button whileTap={tapAnimation} onClick={fetchComplaints}>
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>

        {complaints.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: SBER.green }} />
            <p style={{ color: SBER.gray }}>Нет жалоб</p>
          </div>
        ) : (
          <div className="space-y-3">
            {complaints.map((c, idx) => (
              <div key={idx} className="p-3 rounded-xl" style={{ background: SBER.grayPale }}>
                <p className="text-sm font-medium" style={{ color: SBER.dark }}>{c.subject}</p>
                <p className="text-xs mt-1" style={{ color: SBER.gray }}>{c.description}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${c.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                    {c.status === 'open' ? 'Открыта' : 'Решена'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// Accountant Module - Financial Reports
const AccountantModule = ({ token }) => {
  const [report, setReport] = useState(null);
  const [rates, setRates] = useState({ current: {}, history: [] });
  const [loading, setLoading] = useState(true);
  const [newRate, setNewRate] = useState('');
  const [newBuyFee, setNewBuyFee] = useState('');
  const [newSellFee, setNewSellFee] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, ratesRes] = await Promise.all([
        axios.get(`${API}/admin/accountant/financial-report`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/accountant/exchange-rates`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setReport(reportRes.data);
      setRates(ratesRes.data);
      setNewRate(ratesRes.data.current.usdt_rub_rate?.toString() || '');
      setNewBuyFee(ratesRes.data.current.buy_fee_percent?.toString() || '');
      setNewSellFee(ratesRes.data.current.sell_fee_percent?.toString() || '');
    } catch (err) {
      toast.error('Ошибка загрузки');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleUpdateRates = async () => {
    try {
      await axios.post(`${API}/admin/accountant/exchange-rates`, {
        usdt_rub_rate: parseFloat(newRate),
        buy_fee_percent: parseFloat(newBuyFee),
        sell_fee_percent: parseFloat(newSellFee)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Курс обновлён');
      fetchData();
    } catch (err) {
      toast.error('Ошибка обновления');
    }
  };

  const handleExport = async (format) => {
    try {
      if (format === 'csv') {
        // For CSV, download directly as file
        const response = await fetch(`${API}/admin/reports/export?report_type=transactions&format=csv`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'transactions_report.csv';
        a.click();
        toast.success('CSV отчёт выгружен');
        return;
      }
      
      // JSON export
      const res = await axios.get(`${API}/admin/reports/export?report_type=transactions&format=${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions_report.json';
      a.click();
      toast.success('JSON отчёт выгружен');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="p-4" accent>
        <p className="text-xs text-white/70 uppercase">Оборот за 30 дней</p>
        <p className="text-2xl font-bold text-white mt-1">
          {report?.summary?.total_topup_rub?.toLocaleString() || 0} ₽
        </p>
        <div className="flex gap-4 mt-2">
          <div className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-white/70" />
            <span className="text-sm text-white/90">
              Вход: {report?.summary?.total_topup_rub?.toLocaleString() || 0} ₽
            </span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingDown className="w-4 h-4 text-white/70" />
            <span className="text-sm text-white/90">
              Выход: {report?.summary?.total_withdraw_rub?.toLocaleString() || 0} ₽
            </span>
          </div>
        </div>
      </Card>

      {/* Exchange Rates */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: SBER.dark }}>Управление курсом</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs" style={{ color: SBER.gray }}>Курс USDT/RUB</label>
            <input
              type="number"
              value={newRate}
              onChange={(e) => setNewRate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg mt-1"
              style={{ background: SBER.grayPale }}
              data-testid="rate-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs" style={{ color: SBER.gray }}>Комиссия покупки %</label>
              <input
                type="number"
                value={newBuyFee}
                onChange={(e) => setNewBuyFee(e.target.value)}
                className="w-full px-3 py-2 rounded-lg mt-1"
                style={{ background: SBER.grayPale }}
              />
            </div>
            <div>
              <label className="text-xs" style={{ color: SBER.gray }}>Комиссия продажи %</label>
              <input
                type="number"
                value={newSellFee}
                onChange={(e) => setNewSellFee(e.target.value)}
                className="w-full px-3 py-2 rounded-lg mt-1"
                style={{ background: SBER.grayPale }}
              />
            </div>
          </div>
          <motion.button
            whileTap={tapAnimation}
            onClick={handleUpdateRates}
            className="w-full py-2 rounded-xl font-medium text-white"
            style={{ background: SBER.green }}
            data-testid="update-rate-btn"
          >
            Обновить курс
          </motion.button>
        </div>
      </Card>

      {/* Export */}
      <Card className="p-4">
        <h3 className="font-semibold mb-4" style={{ color: SBER.dark }}>Экспорт отчётов</h3>
        <div className="flex gap-2">
          <motion.button
            whileTap={tapAnimation}
            onClick={() => handleExport('json')}
            className="flex-1 py-2 rounded-xl flex items-center justify-center gap-2"
            style={{ background: SBER.grayPale }}
            data-testid="export-json-btn"
          >
            <Download className="w-4 h-4" />
            JSON
          </motion.button>
          <motion.button
            whileTap={tapAnimation}
            onClick={() => handleExport('csv')}
            className="flex-1 py-2 rounded-xl flex items-center justify-center gap-2"
            style={{ background: SBER.greenPale, color: SBER.green }}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            CSV
          </motion.button>
        </div>
      </Card>

      {/* Stats */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Статистика</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Всего транзакций</span>
            <span className="font-medium">{report?.summary?.total_transactions || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Комиссии</span>
            <span className="font-medium text-green-600">
              {report?.summary?.total_fees_rub?.toLocaleString() || 0} ₽
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Чистый поток</span>
            <span className={`font-medium ${(report?.summary?.net_flow_rub || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {report?.summary?.net_flow_rub?.toLocaleString() || 0} ₽
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Reports Module
const ReportsModule = ({ token }) => {
  const [chartData, setChartData] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('transactions');
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  
  const periods = [
    { value: '24h', label: '24 часа' },
    { value: '7d', label: '7 дней' },
    { value: '30d', label: '30 дней' },
    { value: '90d', label: '90 дней' }
  ];
  
  const metrics = [
    { value: 'transactions', label: 'Транзакции' },
    { value: 'users', label: 'Пользователи' },
    { value: 'kyc', label: 'KYC статусы' }
  ];

  const CHART_COLORS = ['#21A038', '#4DB45F', '#FFA726', '#EF5350', '#42A5F5'];
  
  useEffect(() => {
    fetchChartData();
  }, [selectedPeriod, selectedMetric, token]);
  
  const fetchChartData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/chart-data?period=${selectedPeriod}&metric=${selectedMetric}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChartData(res.data.data || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      console.error('Chart data error:', err);
    }
    setLoading(false);
  };
  
  const handleExport = async (type, format = 'json') => {
    try {
      if (format === 'csv' || format === 'xlsx') {
        const response = await fetch(`${API}/admin/reports/export?report_type=${type}&format=${format}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_report.${format}`;
        a.click();
        toast.success(`${format.toUpperCase()} отчёт выгружен`);
        return;
      }
      
      const res = await axios.get(`${API}/admin/reports/export?report_type=${type}&format=json`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.json`;
      a.click();
      toast.success('JSON отчёт выгружен');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}.${d.getMonth() + 1}`;
  };

  return (
    <div className="space-y-4">
      {/* Analytics Section */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2" style={{ color: SBER.dark }}>
            <BarChart3 className="w-5 h-5" style={{ color: SBER.green }} />
            Аналитика
          </h2>
          <motion.button 
            whileTap={tapAnimation}
            onClick={fetchChartData}
            className="p-2 rounded-lg"
            style={{ background: SBER.grayPale }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.gray }} />
          </motion.button>
        </div>
        
        {/* Period Selector */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {periods.map(p => (
            <motion.button
              key={p.value}
              whileTap={tapAnimation}
              onClick={() => setSelectedPeriod(p.value)}
              className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap"
              style={{ 
                background: selectedPeriod === p.value ? SBER.green : SBER.grayPale,
                color: selectedPeriod === p.value ? 'white' : SBER.dark
              }}
              data-testid={`period-${p.value}`}
            >
              {p.label}
            </motion.button>
          ))}
        </div>
        
        {/* Metric Selector */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {metrics.map(m => (
            <motion.button
              key={m.value}
              whileTap={tapAnimation}
              onClick={() => setSelectedMetric(m.value)}
              className="px-3 py-1.5 rounded-lg text-sm whitespace-nowrap"
              style={{ 
                background: selectedMetric === m.value ? SBER.greenPale : SBER.grayPale,
                color: selectedMetric === m.value ? SBER.green : SBER.gray
              }}
              data-testid={`metric-${m.value}`}
            >
              {m.label}
            </motion.button>
          ))}
        </div>
        
        {/* Chart */}
        <div className="h-64 w-full">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <RefreshCw className="w-8 h-8 animate-spin" style={{ color: SBER.green }} />
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              Нет данных за выбранный период
            </div>
          ) : selectedMetric === 'kyc' ? (
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={chartData}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ status, count }) => `${status}: ${count}`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis tick={{ fontSize: 12 }} stroke="#9CA3AF" />
                <Tooltip 
                  formatter={(value, name) => [
                    name === 'volume' || name === 'topup' || name === 'withdraw' 
                      ? `${value.toLocaleString()} ₽` 
                      : value,
                    name === 'count' ? 'Кол-во' : 
                    name === 'volume' ? 'Объём' :
                    name === 'topup' ? 'Пополнения' :
                    name === 'withdraw' ? 'Выводы' :
                    name === 'kyc_approved' ? 'KYC подтв.' : name
                  ]}
                />
                <Legend />
                {selectedMetric === 'transactions' ? (
                  <>
                    <Bar dataKey="count" name="Кол-во" fill={SBER.green} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="topup" name="Пополнения" fill="#4DB45F" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="withdraw" name="Выводы" fill="#FFA726" radius={[4, 4, 0, 0]} />
                  </>
                ) : (
                  <>
                    <Bar dataKey="count" name="Новые" fill={SBER.green} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="kyc_approved" name="KYC подтв." fill="#4DB45F" radius={[4, 4, 0, 0]} />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        
        {/* Summary */}
        {Object.keys(summary).length > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            {selectedMetric === 'transactions' ? (
              <>
                <div className="p-3 rounded-lg text-center" style={{ background: SBER.grayPale }}>
                  <p className="text-lg font-bold" style={{ color: SBER.dark }}>{summary.total_count || 0}</p>
                  <p className="text-xs" style={{ color: SBER.gray }}>Всего транзакций</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ background: SBER.greenPale }}>
                  <p className="text-lg font-bold" style={{ color: SBER.green }}>{(summary.total_volume || 0).toLocaleString()} ₽</p>
                  <p className="text-xs" style={{ color: SBER.gray }}>Общий объём</p>
                </div>
              </>
            ) : (
              <>
                <div className="p-3 rounded-lg text-center" style={{ background: SBER.grayPale }}>
                  <p className="text-lg font-bold" style={{ color: SBER.dark }}>{summary.total_new || 0}</p>
                  <p className="text-xs" style={{ color: SBER.gray }}>Новых пользователей</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ background: SBER.greenPale }}>
                  <p className="text-lg font-bold" style={{ color: SBER.green }}>{summary.total_kyc_approved || 0}</p>
                  <p className="text-xs" style={{ color: SBER.gray }}>KYC подтверждено</p>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Export Section */}
      <Card className="p-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: SBER.dark }}>Экспорт отчётов</h2>
        
        <div className="space-y-4">
          {/* Transactions Export */}
          <div className="p-4 rounded-xl" style={{ background: SBER.grayPale }}>
            <div className="flex items-center gap-3 mb-3">
              <ArrowLeftRight className="w-5 h-5" style={{ color: SBER.green }} />
              <span className="font-medium" style={{ color: SBER.dark }}>Транзакции</span>
            </div>
            <div className="flex gap-2">
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('transactions', 'json')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.white }}
                data-testid="export-transactions-json"
              >
                <Download className="w-4 h-4" />
                JSON
              </motion.button>
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('transactions', 'csv')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.white }}
                data-testid="export-transactions-csv"
              >
                <Download className="w-4 h-4" />
                CSV
              </motion.button>
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('transactions', 'xlsx')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.greenPale, color: SBER.green }}
                data-testid="export-transactions-xlsx"
              >
                <Download className="w-4 h-4" />
                Excel
              </motion.button>
            </div>
          </div>

          {/* Users Export */}
          <div className="p-4 rounded-xl" style={{ background: SBER.grayPale }}>
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5" style={{ color: SBER.green }} />
              <span className="font-medium" style={{ color: SBER.dark }}>Пользователи</span>
            </div>
            <div className="flex gap-2">
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('users', 'json')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.white }}
                data-testid="export-users-json"
              >
                <Download className="w-4 h-4" />
                JSON
              </motion.button>
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('users', 'csv')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.white }}
                data-testid="export-users-csv"
              >
                <Download className="w-4 h-4" />
                CSV
              </motion.button>
              <motion.button
                whileTap={tapAnimation}
                onClick={() => handleExport('users', 'xlsx')}
                className="flex-1 py-2 rounded-lg flex items-center justify-center gap-2 text-sm"
                style={{ background: SBER.greenPale, color: SBER.green }}
                data-testid="export-users-xlsx"
              >
                <Download className="w-4 h-4" />
                Excel
              </motion.button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Dashboard for different roles - Enhanced
const RoleDashboard = ({ role, token, analytics }) => {
  const config = roleConfig[role];
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await axios.get(`${API}/admin/dashboard/full`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboardData(res.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      }
      setLoading(false);
    };
    if (token) fetchDashboard();
  }, [token]);

  const data = dashboardData || analytics || {};
  
  return (
    <div className="space-y-4">
      {/* Role Badge */}
      <Card className="p-4" accent>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)' }}>
            {config && <config.icon className="w-6 h-6 text-white" />}
          </div>
          <div>
            <p className="text-white/70 text-xs">Ваша роль</p>
            <p className="text-white font-bold text-lg">{config?.label || 'Администратор'}</p>
          </div>
        </div>
      </Card>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 text-center" data-testid="stat-users">
          <Users className="w-6 h-6 mx-auto mb-2" style={{ color: SBER.green }} />
          <p className="text-xl font-bold" style={{ color: SBER.dark }}>
            {data?.users?.total || 0}
          </p>
          <p className="text-xs" style={{ color: SBER.gray }}>Пользователей</p>
        </Card>
        <Card className="p-4 text-center" data-testid="stat-transactions">
          <ArrowLeftRight className="w-6 h-6 mx-auto mb-2" style={{ color: SBER.green }} />
          <p className="text-xl font-bold" style={{ color: SBER.dark }}>
            {data?.transactions?.today || 0}
          </p>
          <p className="text-xs" style={{ color: SBER.gray }}>Транзакций сегодня</p>
        </Card>
      </div>

      {/* Alerts Section */}
      {(data?.alerts?.kyc_pending > 0 || data?.alerts?.aml_flags > 0 || data?.alerts?.open_tickets > 0) && (
        <Card className="p-4" data-testid="alerts-section">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: SBER.dark }}>
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Требуют внимания
          </h3>
          <div className="space-y-2">
            {data?.alerts?.kyc_pending > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#FEF3C7' }}>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800">KYC на проверке</span>
                </div>
                <span className="font-bold text-amber-800">{data.alerts.kyc_pending}</span>
              </div>
            )}
            {data?.alerts?.aml_flags > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#FEE2E2' }}>
                <div className="flex items-center gap-2">
                  <Flag className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-800">AML флаги</span>
                </div>
                <span className="font-bold text-red-800">{data.alerts.aml_flags}</span>
              </div>
            )}
            {data?.alerts?.open_tickets > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#DBEAFE' }}>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm text-blue-800">Открытые тикеты</span>
                </div>
                <span className="font-bold text-blue-800">{data.alerts.open_tickets}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* User Stats */}
      <Card className="p-4" data-testid="user-stats">
        <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Статистика пользователей</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Telegram</span>
            <span className="font-medium" style={{ color: SBER.dark }}>{data?.users?.telegram || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Web</span>
            <span className="font-medium" style={{ color: SBER.dark }}>{data?.users?.web || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Новые сегодня</span>
            <span className="font-medium text-green-600">+{data?.users?.new_today || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>KYC пройден</span>
            <span className="font-medium" style={{ color: SBER.green }}>{data?.users?.kyc_approved || 0}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: SBER.gray }}>Заблокировано</span>
            <span className="font-medium text-red-500">{data?.users?.blocked || 0}</span>
          </div>
        </div>
      </Card>

      {/* Volume Stats (for admin/accountant roles) */}
      {['super_admin', 'administrator', 'admin', 'accountant', 'accounting', 'settlements'].includes(role) && (
        <Card className="p-4" data-testid="volume-stats">
          <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Объём за сегодня</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm" style={{ color: SBER.gray }}>Пополнения</span>
              </div>
              <span className="font-medium text-green-600">
                {(data?.volume?.today_topup || 0).toLocaleString()} ₽
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-amber-500" />
                <span className="text-sm" style={{ color: SBER.gray }}>Выводы</span>
              </div>
              <span className="font-medium text-amber-600">
                {(data?.volume?.today_withdraw || 0).toLocaleString()} ₽
              </span>
            </div>
            <div className="border-t pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: SBER.gray }}>Нетто</span>
                <span className={`font-bold ${(data?.volume?.today_net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(data?.volume?.today_net || 0) >= 0 ? '+' : ''}{(data?.volume?.today_net || 0).toLocaleString()} ₽
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Transaction Stats */}
      <Card className="p-4" data-testid="transaction-stats">
        <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Транзакции</h3>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-lg" style={{ background: SBER.grayPale }}>
            <p className="text-lg font-bold" style={{ color: SBER.dark }}>{data?.transactions?.total || 0}</p>
            <p className="text-xs" style={{ color: SBER.gray }}>Всего</p>
          </div>
          <div className="p-2 rounded-lg" style={{ background: '#FEF3C7' }}>
            <p className="text-lg font-bold text-amber-700">{data?.transactions?.pending || 0}</p>
            <p className="text-xs text-amber-600">В обработке</p>
          </div>
          <div className="p-2 rounded-lg" style={{ background: '#FEE2E2' }}>
            <p className="text-lg font-bold text-red-700">{data?.transactions?.failed_today || 0}</p>
            <p className="text-xs text-red-600">Ошибки сегодня</p>
          </div>
        </div>
      </Card>

      {/* Quick Actions by Role */}
      {['compliance', 'kyc_manager'].includes(role) && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3" style={{ color: SBER.dark }}>Быстрые действия KYC</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: SBER.grayPale }}>
              <span>KYC на проверке</span>
              <span className="font-bold">{data?.users?.kyc_pending || 0}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

// Settings Module (Admin only)
const SettingsModule = ({ token, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'super_admin', full_name: '' });
  const [loading, setLoading] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (err) {}
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/create`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Пользователь создан');
      setShowCreate(false);
      setNewUser({ username: '', password: '', role: 'compliance', full_name: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка создания');
    }
    setLoading(false);
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold" style={{ color: SBER.dark }}>Управление администраторами</h2>
          <motion.button
            whileTap={tapAnimation}
            onClick={() => setShowCreate(!showCreate)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-white"
            style={{ background: SBER.green }}
          >
            + Добавить
          </motion.button>
        </div>

        {showCreate && (
          <div className="mb-4 p-4 rounded-xl space-y-3" style={{ background: SBER.grayPale }}>
            <input
              type="text"
              placeholder="Логин"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              className="w-full px-3 py-2 rounded-lg"
              data-testid="new-admin-username"
            />
            <input
              type="password"
              placeholder="Пароль"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="w-full px-3 py-2 rounded-lg"
              data-testid="new-admin-password"
            />
            <input
              type="text"
              placeholder="ФИО"
              value={newUser.full_name}
              onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="w-full px-3 py-2 rounded-lg"
              data-testid="new-admin-role"
            >
              <option value="super_admin">Суперадмин</option>
              <option value="admin">Админ</option>
              <option value="kyc_manager">KYC-менеджер</option>
              <option value="settlements">Отдел расчётов</option>
              <option value="accounting">Бухгалтерия</option>
              <option value="manager">Менеджер</option>
            </select>
            <motion.button
              whileTap={tapAnimation}
              onClick={handleCreate}
              disabled={loading}
              className="w-full py-2 rounded-lg font-medium text-white"
              style={{ background: SBER.green }}
              data-testid="create-admin-btn"
            >
              {loading ? 'Создание...' : 'Создать'}
            </motion.button>
          </div>
        )}

        <div className="space-y-2">
          {users.map((user, idx) => (
            <div key={idx} className="p-3 rounded-xl flex items-center justify-between" style={{ background: SBER.grayPale }}>
              <div>
                <p className="font-medium" style={{ color: SBER.dark }}>{user.full_name || user.username}</p>
                <p className="text-xs" style={{ color: SBER.gray }}>
                  @{user.username} • {roleConfig[user.role]?.label || user.role}
                </p>
              </div>
              <span 
                className="text-xs px-2 py-1 rounded-full"
                style={{ 
                  background: user.is_active ? SBER.greenPale : '#FEE2E2',
                  color: user.is_active ? SBER.green : '#DC2626'
                }}
              >
                {user.is_active ? 'Активен' : 'Заблокирован'}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="text-lg font-bold mb-4" style={{ color: SBER.dark }}>Юридические документы</h2>
        <a 
          href="/pro/documents"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-medium"
          style={{ background: SBER.greenPale, color: SBER.green }}
          data-testid="documents-link"
        >
          <FileText className="w-5 h-5" />
          Открыть документы B2B
        </a>
        <p className="text-xs mt-2 text-center" style={{ color: SBER.gray }}>
          Оферта, политики, лицензии для юридических лиц
        </p>
      </Card>

      <Card className="p-4">
        <motion.button
          whileTap={tapAnimation}
          onClick={onLogout}
          className="w-full py-3 rounded-xl flex items-center justify-center gap-2 font-medium"
          style={{ background: '#FEE2E2', color: '#DC2626' }}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          Выйти
        </motion.button>
      </Card>
    </div>
  );
};

// Main Component
const RoleBasedAdmin = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [role, setRole] = useState(localStorage.getItem('admin_role') || '');
  const [username, setUsername] = useState(localStorage.getItem('admin_username') || '');
  const [activePage, setActivePage] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = (newToken, newRole, newUsername) => {
    setToken(newToken);
    setRole(newRole);
    setUsername(newUsername);
    localStorage.setItem('admin_token', newToken);
    localStorage.setItem('admin_role', newRole);
    localStorage.setItem('admin_username', newUsername);
  };

  const handleLogout = () => {
    setToken('');
    setRole('');
    setUsername('');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_username');
    setActivePage('dashboard');
  };

  const fetchAnalytics = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token]);

  if (!token) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard':
        return <RoleDashboard role={role} token={token} analytics={analytics} />;
      case 'users':
        return <UsersModule token={token} role={role} />;
      case 'compliance':
        return <ComplianceModule token={token} />;
      case 'aml':
        return <AMLModule token={token} />;
      case 'lawyer':
        return <LawyerModule token={token} />;
      case 'complaints':
        return <ComplaintsModule token={token} />;
      case 'accountant':
        return <AccountantModule token={token} />;
      case 'reports':
        return <ReportsModule token={token} />;
      case 'settings':
        return <SettingsModule token={token} onLogout={handleLogout} />;
      default:
        return <RoleDashboard role={role} token={token} analytics={analytics} />;
    }
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: SBER.bg }}>
      <Toaster position="top-center" richColors />
      {/* Header */}
      <div className="px-5 pt-6 pb-4" style={{ background: SBER.white }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: SBER.dark }}>PolyTech Admin</h1>
            <p className="text-xs" style={{ color: SBER.gray }}>
              {roleConfig[role]?.label || 'Администратор'} • {username}
            </p>
          </div>
          <motion.button
            whileTap={tapAnimation}
            onClick={fetchAnalytics}
            disabled={loading}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.greenPale }}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} style={{ color: SBER.green }} />
          </motion.button>
        </div>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={activePage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation */}
      <RoleNavigation 
        role={role} 
        active={activePage} 
        onNavigate={setActivePage} 
      />
    </div>
  );
};

export default RoleBasedAdmin;
